import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import type { FieldError, Resolver } from 'react-hook-form';
import { Package, Truck, User, CheckCircle, Printer, AlertTriangle, MapPin } from 'lucide-react';
import { useAuth } from 'react-oidc-context';
import { FormInput } from './FormInput';
import { FormTextarea } from './FormTextarea';
import { FormSelect } from './FormSelect';
import {
  placesEnabled,
  validateShippingAddress,
  autocompleteAddress,
  fetchPlaceAddress,
  isRealPremise,
  type ResolvedAddress,
  type AddressSuggestion,
} from '../../lib/googlePlaces';
import { Button } from '../ui/Button';
import { SubmissionProgress } from '../ui/SubmissionProgress';
import { useAuthUser } from '../../hooks/useAuthUser';
import { isOidcConfigured } from '../../lib/auth';
import { type ShipRecord, openPrintWindow } from '../../lib/printShipment';
import {
  shipRequestSchema,
  type ShipRequestFormValues,
  carrierOptions,
  priorityOptions,
  shippingChargesOptions,
  shipmentContainsOptions,
  departmentOptions,
} from '../../lib/validation';

const shipRequestEndpoint = '/api/ship-request';

const countryOptions = [
  { value: 'UNITED STATES', label: 'United States' },
  { value: 'Canada', label: 'Canada' },
  { value: 'Mexico', label: 'Mexico' },
  { value: 'Japan', label: 'Japan' },
  { value: 'Costa Rica', label: 'Costa Rica' },
  { value: 'Puerto Rico', label: 'Puerto Rico' },
  { value: 'Other', label: 'Other' },
];

const defaultFormValues: Partial<ShipRequestFormValues> = {
  country: 'UNITED STATES',
  carrier: 'UPS',
  priority: 'Ground',
  shippingCharges: 'Prepaid',
  insuranceAmt: 0,
};

export function ShipRequestForm() {
  const navigate = useNavigate();
  const authUser = useAuthUser();
  const auth = isOidcConfigured() ? useAuth() : null; // eslint-disable-line react-hooks/rules-of-hooks
  const [submissionStatus, setSubmissionStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [submissionError, setSubmissionError] = useState('');
  const [submittedPayload, setSubmittedPayload] = useState<ShipRecord | null>(null);
  // Google address verification: soft gate. An unverified address shows a
  // choice card (use Google's match / submit as entered) — never a hard block.
  const [addressIssue, setAddressIssue] = useState<{
    suggested?: ResolvedAddress;
    /** Fallback "did you mean" matches from Places search when Google has no correction. */
    alternatives?: AddressSuggestion[];
    /** Places session token shared by the alternatives search + details fetch. */
    altToken?: string;
    /** Friendly names of the parts Google couldn't confirm (e.g. "street name"). */
    unconfirmed?: string[];
    /** Parts Google silently rewrote, e.g. a misspelled state it "fixed". */
    corrected?: string[];
  } | null>(null);
  const [addressVerified, setAddressVerified] = useState(false);
  // Ref, not state: must be readable synchronously inside onSubmit
  const overrideRef = useRef(false);

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    reset,
    getValues,
    formState: { errors },
  } = useForm<ShipRequestFormValues>({
    resolver: zodResolver(shipRequestSchema) as Resolver<ShipRequestFormValues>,
    defaultValues: { ...defaultFormValues, senderEmail: authUser.email || '' },
  });

  useEffect(() => {
    if (authUser.isAuthenticated && authUser.email) {
      setValue('senderEmail', authUser.email);
    }
  }, [authUser.isAuthenticated, authUser.email, setValue]);

  const selectedCarrier = watch('carrier');
  const selectedShipmentContains = watch('shipmentContains');

  // Address Lines 1 & 2 are capped at 50 characters (stricter than the DB columns).
  const ADDRESS1_MAX = 50;
  const address1Length = (watch('address1') ?? '').length;
  const ADDRESS2_MAX = 50;
  const address2Length = (watch('address2') ?? '').length;

  // Any edit to the address invalidates a previous verdict (warning card,
  // verified chip, and "send as entered" override all reset)
  const watchedAddress = watch(['address1', 'address2', 'city', 'state', 'zipCode', 'country']);
  const watchedAddressKey = JSON.stringify(watchedAddress);
  useEffect(() => {
    setAddressIssue(null);
    setAddressVerified(false);
    overrideRef.current = false;
  }, [watchedAddressKey]);

  const applyResolvedAddress = (addr: ResolvedAddress) => {
    const opts = { shouldValidate: true, shouldDirty: true } as const;
    setValue('address1', addr.address1.slice(0, ADDRESS1_MAX), opts);
    if (addr.address2) setValue('address2', addr.address2.slice(0, ADDRESS2_MAX), opts);
    if (addr.city) setValue('city', addr.city, opts);
    if (addr.state) setValue('state', addr.state, opts);
    if (addr.zipCode) setValue('zipCode', addr.zipCode, opts);
    setValue('country', addr.country, opts);
  };

  const onSubmit = async (data: ShipRequestFormValues) => {
    setSubmissionStatus('submitting');
    setSubmissionError('');

    // Verify the address with Google before sending. Soft gate: on failure we
    // show a choice card (apply Google's match / submit as entered). Countries
    // the Address Validation API doesn't cover are skipped.
    let addressWasOverridden = false;
    if (placesEnabled && !overrideRef.current) {
      const check = await validateShippingAddress({
        address1: data.address1,
        address2: data.address2,
        city: data.city,
        state: data.state,
        zipCode: data.zipCode,
        country: data.country,
      });
      if (check.status === 'unverified') {
        // A "suggestion" that just echoes the input back isn't a correction
        const s = check.suggested;
        const sameAsInput =
          s &&
          s.address1.trim().toLowerCase() === data.address1.trim().toLowerCase() &&
          s.city.trim().toLowerCase() === data.city.trim().toLowerCase() &&
          s.state.trim().toLowerCase() === data.state.trim().toLowerCase() &&
          s.zipCode.trim() === data.zipCode.trim();
        const suggested = sameAsInput ? undefined : s;

        // No usable correction → offer real addresses near the input via Places
        // search. Predictions can extrapolate street numbers that don't exist,
        // so each candidate is validated and only confirmed premises are shown.
        let alternatives: AddressSuggestion[] = [];
        const altToken = crypto.randomUUID();
        if (!suggested) {
          try {
            const candidates = (
              await autocompleteAddress(
                `${data.address1}, ${data.city}, ${data.state} ${data.zipCode}`,
                altToken,
              )
            ).slice(0, 5);
            const real = await Promise.all(
              candidates.map((c) => isRealPremise(c.text, data.country)),
            );
            alternatives = candidates.filter((_, i) => real[i]).slice(0, 3);
          } catch {
            /* fall through with no alternatives */
          }
        }

        setAddressIssue({
          suggested, alternatives, altToken,
          unconfirmed: check.unconfirmed,
          corrected: check.corrected,
        });
        setSubmissionStatus('idle');
        return;
      }
      if (check.status === 'verified') setAddressVerified(true);
    } else if (placesEnabled && overrideRef.current) {
      addressWasOverridden = true;
    }

    // SentBy = star email without @starmicronics.com
    const sentBy = data.senderEmail.replace(/@starmicronics\.com$/i, '');

    // Map form fields to DB column names
    // email  = customer/recipient email
    // Email2 = star employee email
    const submittedAt = new Date().toISOString();

    const noteParts: string[] = [];
    if (data.notes?.trim()) noteParts.push(data.notes.trim());
    noteParts.push(`Need Return Slip: ${data.needReturnSlip ? 'Yes' : 'No'}`);
    if (data.needsToArriveBy) noteParts.push(`Needs to Arrive By: ${data.needsToArriveBy}`);
    // Ops signal for the ship desk: this address failed Google verification
    if (addressWasOverridden) {
      noteParts.push('ADDRESS NOT VERIFIED: Google could not confirm this address — submitted as entered by requester. Please double-check before shipping.');
    }

    const payload = {
      SDate:            submittedAt,
      Department:       data.department,
      SentBy:           sentBy,
      Name:             data.shipToName,
      Company:          data.company ?? null,
      Address1:         data.address1,
      Address2:         data.address2 ?? null,
      City:             data.city,
      State:            data.state,
      ZipCode:          data.zipCode,
      Country:          data.country,
      email:            data.customerEmail ?? null,   // customer email
      PhoneNumber:      data.phoneNumber ?? null,
      PO:               data.po ?? null,
      Carrier:          data.carrier === 'Other(Specify)' ? (data.carrierOther || 'Other') : data.carrier,
      Priority:         data.priority,
      ShippingCharges:  data.shippingCharges,
      UPSAccount:       data.upsAccount ?? null,
      InsuranceAmt:     data.insuranceAmt ?? 0,
      ShipmentContains: data.shipmentContains === 'Other(specify)' ? (data.shipmentContainsOther || 'Other') : (data.shipmentContains ?? null),
      TrackingNo:       null,
      UPSCharges:       0,
      Confirmed:        true,
      ConfirmedDate:    submittedAt,
      Notes:            noteParts.length > 0 ? noteParts.join('\n') : null,
      Email2:           data.senderEmail,             // star employee email
    };

    try {
      const accessToken = auth?.user?.access_token;
      const response = await fetch(shipRequestEndpoint, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Submission failed (${response.status}). Please try again.`);
      }

      // Start with what we sent; try to upgrade with the full WMS record (which has InvoiceNo)
      let confirmedRecord: ShipRecord = { ...payload };

      // 1. Check if the PATCH response itself returned the record with InvoiceNo
      try {
        const responseData = await response.json();
        if (responseData?.InvoiceNo) {
          confirmedRecord = { ...payload, InvoiceNo: responseData.InvoiceNo };
        }
      } catch { /* response wasn't JSON or had no InvoiceNo */ }

      // 2. If still no InvoiceNo, poll ship-history until the WMS record appears
      if (!confirmedRecord.InvoiceNo) {
        for (let attempt = 0; attempt < 5; attempt++) {
          await new Promise<void>((r) => setTimeout(r, 1500));
          try {
            const histRes = await fetch(`/api/ship-history?_cb=${Date.now()}`);
            if (histRes.ok) {
              const records: ShipRecord[] = await histRes.json();
              const match = records.find(
                (r) =>
                  r.Email2?.toLowerCase() === data.senderEmail.toLowerCase() &&
                  Math.abs(new Date(r.SDate).getTime() - new Date(submittedAt).getTime()) < 120_000,
              );
              if (match?.InvoiceNo) {
                confirmedRecord = match;
                break;
              }
            }
          } catch { /* ignore polling errors, keep trying */ }
        }
      }

      setSubmittedPayload(confirmedRecord);
      setSubmissionStatus('success');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
      setSubmissionError(msg);
      setSubmissionStatus('error');
    }
  };

  const deptOptions = departmentOptions.map((d) => ({ value: d, label: d }));

  const handlePrint = () => {
    if (!submittedPayload) return;
    openPrintWindow(submittedPayload);
  };

  if (submissionStatus === 'success' && submittedPayload) {
    return (
      <div className="flex flex-col items-center text-center gap-6 py-4">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-1">Request Submitted!</h2>
          <p className="text-gray-500 text-sm">
            Ship to <span className="font-medium text-gray-700">{submittedPayload.Name}</span>
            {submittedPayload.Company ? ` — ${submittedPayload.Company}` : ''}
          </p>
        </div>
        <div className="w-full bg-gray-50 border border-border rounded-xl p-4 text-left space-y-1.5 text-sm">
          <p><span className="text-muted text-xs uppercase font-semibold tracking-wide">Carrier</span><br /><span className="text-gray-800 font-medium">{submittedPayload.Carrier} · {submittedPayload.Priority}</span></p>
          <p><span className="text-muted text-xs uppercase font-semibold tracking-wide">Address</span><br /><span className="text-gray-800">{[submittedPayload.City, submittedPayload.State, submittedPayload.Country].filter(Boolean).join(', ')}</span></p>
          <p><span className="text-muted text-xs uppercase font-semibold tracking-wide">Submitted by</span><br /><span className="text-gray-800">{submittedPayload.SentBy} · {submittedPayload.Department}</span></p>
        </div>
        <div className="flex gap-3 w-full">
          <Button
            type="button"
            onClick={handlePrint}
            className="flex-1 gap-2"
          >
            <Printer className="w-4 h-4" />
            Print Request
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="flex-1"
            onClick={() => {
              // Start the next request blank, but keep who's sending it
              const { senderEmail, department } = getValues();
              reset({ ...defaultFormValues, senderEmail, department });
              setSubmissionStatus('idle');
              setSubmittedPayload(null);
              window.scrollTo({ top: 0 });
            }}
          >
            Submit Another
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <SubmissionProgress
        status={submissionStatus}
        errorMessage={submissionError}
        onDismissError={() => setSubmissionStatus('idle')}
      />

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">

        {/* ── Ship To ──────────────────────────────────────────────── */}
        <div className="space-y-5">
          <div className="flex items-center gap-3 border-b border-border pb-3">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Package className="w-4 h-4 text-primary" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Ship To</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <FormInput
              label="Recipient Name"
              placeholder="Full name"
              {...register('shipToName')}
              error={errors.shipToName}
              required
            />
            <FormInput
              label="Company"
              placeholder="Company name (optional)"
              {...register('company')}
              error={errors.company}
            />
          </div>

          {/* Plain input on purpose — no as-you-type Places autocomplete here.
              Address checking happens once, on submit (see onSubmit): a verified
              chip, or the unverified choice card with its "did you mean" rows. */}
          <FormInput
            label="Address Line 1"
            placeholder="Street address"
            maxLength={ADDRESS1_MAX}
            {...register('address1')}
            error={errors.address1}
            helperText={`Maximum ${ADDRESS1_MAX} characters (${address1Length}/${ADDRESS1_MAX}).`}
            warning={
              address1Length >= ADDRESS1_MAX
                ? `You've reached the ${ADDRESS1_MAX}-character limit for Address Line 1.`
                : undefined
            }
            required
          />
          <FormInput
            label="Address Line 2"
            placeholder="Suite, floor, unit (optional)"
            maxLength={ADDRESS2_MAX}
            {...register('address2')}
            error={errors.address2}
            helperText={`Maximum ${ADDRESS2_MAX} characters (${address2Length}/${ADDRESS2_MAX}).`}
            warning={
              address2Length >= ADDRESS2_MAX
                ? `You've reached the ${ADDRESS2_MAX}-character limit for Address Line 2.`
                : undefined
            }
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <FormInput
              label="City"
              placeholder="City"
              {...register('city')}
              error={errors.city}
              required
            />
            <FormInput
              label="State / Province"
              placeholder="State"
              {...register('state')}
              error={errors.state}
              required
            />
            <FormInput
              label="Zip / Postal Code"
              placeholder="Zip code"
              {...register('zipCode')}
              error={errors.zipCode}
              required
            />
          </div>

          {addressVerified && (
            <div className="flex items-center gap-1.5 text-sm text-green-700">
              <CheckCircle className="w-4 h-4" />
              Address verified
            </div>
          )}

          <Controller
            name="country"
            control={control}
            render={({ field }) => (
              <FormSelect
                label="Country"
                options={countryOptions}
                placeholder="Select country"
                value={field.value ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                name={field.name}
                error={errors.country as FieldError}
                required
              />
            )}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <FormInput
              label="Customer Email"
              type="email"
              placeholder="customer@example.com"
              {...register('customerEmail')}
              error={errors.customerEmail as FieldError}
              helperText="Recipient's email for shipping notifications"
            />
            <FormInput
              label="Phone Number"
              placeholder="(000) 000-0000"
              {...register('phoneNumber')}
              error={errors.phoneNumber}
            />
          </div>

        </div>

        {/* ── Your Info ─────────────────────────────────────────────── */}
        <div className="space-y-5">
          <div className="flex items-center gap-3 border-b border-border pb-3">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <User className="w-4 h-4 text-primary" />
            </div>
            <div className="flex items-center justify-between flex-1">
              <h3 className="text-lg font-semibold text-gray-900">Your Information</h3>
              {authUser.isAuthenticated && (
                <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded-sm">
                  ✓ Signed in via OneLogin
                </span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <FormInput
              label="Your Email"
              type="email"
              placeholder="you@starmicronics.com"
              {...register('senderEmail')}
              error={errors.senderEmail}
              required
              readOnly={authUser.isAuthenticated && !!authUser.email}
              className={authUser.isAuthenticated && authUser.email ? 'bg-gray-50' : ''}
              helperText={authUser.isAuthenticated ? 'Auto-filled from OneLogin' : undefined}
            />
            <Controller
              name="department"
              control={control}
              render={({ field }) => (
                <FormSelect
                  label="Department"
                  options={deptOptions}
                  placeholder="Select your department"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  name={field.name}
                  error={errors.department as FieldError}
                  required
                />
              )}
            />
          </div>
        </div>

        {/* ── Shipping Details ──────────────────────────────────────── */}
        <div className="space-y-5">
          <div className="flex items-center gap-3 border-b border-border pb-3">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Truck className="w-4 h-4 text-primary" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Shipping Details</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className="flex flex-col gap-2">
              <Controller
                name="carrier"
                control={control}
                render={({ field }) => (
                  <FormSelect
                    label="Carrier"
                    options={carrierOptions.map((c) => ({ value: c, label: c }))}
                    placeholder="Select carrier"
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    name={field.name}
                    error={errors.carrier as FieldError}
                    required
                  />
                )}
              />
              {selectedCarrier === 'Other(Specify)' && (
                <FormInput
                  label="Specify carrier"
                  placeholder="e.g. OnTrac, Spee-Dee, LSO…"
                  {...register('carrierOther')}
                  error={errors.carrierOther}
                  required
                />
              )}
            </div>
            <Controller
              name="priority"
              control={control}
              render={({ field }) => (
                <FormSelect
                  label="Priority"
                  options={priorityOptions.map((p) => ({ value: p, label: p }))}
                  placeholder="Select priority"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  name={field.name}
                  error={errors.priority as FieldError}
                  required
                />
              )}
            />
            <Controller
              name="shippingCharges"
              control={control}
              render={({ field }) => (
                <FormSelect
                  label="Shipping Charges"
                  options={shippingChargesOptions.map((s) => ({ value: s, label: s }))}
                  placeholder="Select charges type"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  name={field.name}
                  error={errors.shippingCharges as FieldError}
                  required
                />
              )}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <FormInput
              label="UPS / Carrier Account #"
              placeholder="Account number (if collect)"
              {...register('upsAccount')}
              error={errors.upsAccount}
              helperText="Required if charges are Collect or Third Party"
            />
            <FormInput
              label="Insurance Amount ($)"
              type="number"
              placeholder="0.00"
              {...register('insuranceAmt')}
              error={errors.insuranceAmt}
            />
            <FormInput
              label="PO Number"
              placeholder="PO # (if applicable)"
              {...register('po')}
              error={errors.po}
            />
          </div>

          <div className="flex flex-col gap-2">
            <Controller
              name="shipmentContains"
              control={control}
              render={({ field }) => (
                <FormSelect
                  label="Shipment Contains"
                  options={shipmentContainsOptions.map((o) => ({ value: o, label: o }))}
                  placeholder="Select shipment contents"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  name={field.name}
                  error={errors.shipmentContains as FieldError}
                />
              )}
            />
            {selectedShipmentContains === 'Other(specify)' && (
              <FormInput
                label="Specify contents"
                placeholder="Describe what the shipment contains…"
                {...register('shipmentContainsOther')}
                error={errors.shipmentContainsOther}
                required
              />
            )}
          </div>

        </div>

        {/* ── Notes ────────────────────────────────────────────────── */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 border-b border-border pb-3">
            Notes
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Need Return Slip */}
            <div className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-gray-700">Need Return Slip</span>
              <label className="flex items-center gap-3 cursor-pointer rounded-lg border border-border px-4 py-3 hover:bg-gray-50 transition-colors">
                <input
                  type="checkbox"
                  {...register('needReturnSlip')}
                  className="w-4 h-4 rounded border-gray-300 text-primary accent-primary focus:ring-interactive"
                />
                <span className="text-sm text-gray-600">Yes, include a return slip</span>
              </label>
            </div>

            {/* Needs to Arrive By */}
            <FormInput
              label="Needs to Arrive By"
              type="date"
              {...register('needsToArriveBy')}
              error={errors.needsToArriveBy}
            />
          </div>

          <FormTextarea
            label="Special Instructions / Notes"
            placeholder="e.g. Fragile items, call before delivery..."
            rows={4}
            {...register('notes')}
            error={errors.notes}
          />
        </div>

        {/* ── Actions ──────────────────────────────────────────────── */}
        {addressIssue && (() => {
          const suggestion = addressIssue.suggested;
          const alternatives = addressIssue.alternatives ?? [];
          const unconfirmedList =
            addressIssue.unconfirmed && addressIssue.unconfirmed.length > 0
              ? addressIssue.unconfirmed.join(' and ')
              : null;
          /* Different failure from "couldn't confirm": the address resolves fine,
             but only because Google rewrote something the requester typed. */
          const correctedList =
            addressIssue.corrected && addressIssue.corrected.length > 0
              ? addressIssue.corrected.join(' and ')
              : null;

          const applySuggested = () => {
            if (!suggestion) return;
            applyResolvedAddress(suggestion);
            setAddressIssue(null);
            void handleSubmit(onSubmit)(); // re-validates with the corrected address
          };
          const applyAlternative = async (alt: AddressSuggestion) => {
            const token = addressIssue.altToken ?? crypto.randomUUID();
            setAddressIssue(null);
            try {
              const resolved = await fetchPlaceAddress(alt.placeId, token);
              if (resolved) {
                // Route-level places have no street_number component — keep the
                // number from the prediction text so "9999 Somerset Ave" doesn't
                // collapse to just "Somerset Ave"
                const firstSegment = alt.text.split(',')[0].trim();
                const address1 =
                  !/^\d/.test(resolved.address1) && /^\d/.test(firstSegment)
                    ? firstSegment
                    : resolved.address1;
                applyResolvedAddress({ ...resolved, address1 });
                void handleSubmit(onSubmit)();
              }
            } catch {
              /* keep whatever the user typed */
            }
          };
          const submitAsEntered = () => {
            overrideRef.current = true;
            setAddressIssue(null);
            void handleSubmit(onSubmit)();
          };

          return (
            <div className="p-5 bg-red-50 border border-red-300 rounded-xl space-y-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-red-900">
                    {correctedList
                      ? `Check the ${correctedList} on this address`
                      : "We couldn't verify this address"}
                  </p>
                  <p className="text-sm text-red-800 mt-0.5">
                    {correctedList
                      ? `Google had to change the ${correctedList} to make this address resolve, so what you typed doesn't match a real location. `
                      : ''}
                    {unconfirmedList
                      ? `Google couldn't confirm the ${unconfirmedList}. `
                      : ''}
                    {suggestion
                      ? "Use Google's closest match, or submit the address exactly as you entered it."
                      : alternatives.length > 0
                        ? 'Pick a nearby match below, or submit the address exactly as you entered it.'
                        : 'Double-check it below, or submit it exactly as you entered it.'}
                  </p>
                </div>
              </div>

              {suggestion && (
                <div className="bg-white/70 border border-green-200 rounded-lg p-3 text-sm text-gray-800">
                  <p className="text-[11px] uppercase tracking-wide font-semibold text-green-700 mb-1">Google's suggested address</p>
                  <p>{suggestion.address1}{suggestion.address2 ? `, ${suggestion.address2}` : ''}</p>
                  <p>{[suggestion.city, suggestion.state, suggestion.zipCode].filter(Boolean).join(', ')}</p>
                  <p>{suggestion.country}</p>
                </div>
              )}

              {!suggestion && alternatives.length > 0 && (
                <div className="bg-white/70 border border-red-200 rounded-lg overflow-hidden">
                  <p className="text-[11px] uppercase tracking-wide font-semibold text-red-700 px-3 pt-3 pb-1">Did you mean</p>
                  <ul className="divide-y divide-red-50">
                    {alternatives.map((alt) => (
                      <li key={alt.placeId}>
                        <button
                          type="button"
                          onClick={() => void applyAlternative(alt)}
                          className="group w-full flex items-center justify-between gap-3 px-3 py-2.5 text-left text-sm text-gray-800 hover:bg-red-50 transition-colors"
                        >
                          <span className="flex items-start gap-2.5 min-w-0">
                            <MapPin className="w-4 h-4 mt-0.5 text-red-400 shrink-0" />
                            {alt.text}
                          </span>
                          <span className="flex items-center gap-1 shrink-0 px-3 py-1.5 text-xs font-medium text-white bg-primary rounded-lg group-hover:bg-primary/90 transition-colors">
                            <CheckCircle className="w-3.5 h-3.5" />
                            Use this address
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex items-start gap-2.5 p-3 bg-red-100/80 border border-red-200 rounded-lg">
                <AlertTriangle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                <p className="text-sm text-red-900">
                  <span className="font-semibold">Shipping to an unverified address may incur additional carrier fees.</span>{' '}
                  Please confirm the address with the receiver before submitting.
                </p>
              </div>

              <div className="flex flex-wrap justify-end gap-3">
                {suggestion && (
                  <button
                    type="button"
                    onClick={applySuggested}
                    className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Use suggested address
                  </button>
                )}
                <button
                  type="button"
                  onClick={submitAsEntered}
                  className="px-4 py-2 text-sm font-medium text-red-700 border border-red-300 rounded-lg hover:bg-red-100 transition-colors"
                >
                  Submit as entered
                </button>
              </div>
            </div>
          );
        })()}
        <div className="flex justify-end gap-4 pt-4 border-t border-gray-100">
          <Button type="button" variant="ghost" onClick={() => navigate('/portal')}>
            Cancel
          </Button>
          <Button type="submit" loading={submissionStatus === 'submitting'}>
            Submit Ship Request
          </Button>
        </div>
      </form>
    </>
  );
}
